<template>
    <div>
        <SnackbarComponent v-model="snackbar.show" :message="snackbar.message" :color="snackbar.color" />
        <v-dialog v-model="dialog" max-width="800px" persistent>
            <v-card>
                <v-card-title class="text-h6 pa-4">Edit Testimonial</v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-6">
                    <v-form ref="form">
                        <v-row dense>
                            <v-col cols="12">
                                <div class="rich-text-editor">
                                    <v-text-field v-model="formData.testimonialsUserDesignation"
                                        label="User Designation" variant="outlined" density="compact" />
                                    <label class="v-label text-body-2 mb-2 d-block">
                                        Testimonial Message<span class="text-error">*</span>
                                    </label>
                                    <v-card variant="outlined" class="editor-container">
                                        <div class="editor-toolbar">
                                            <v-btn icon size="small" variant="text"
                                                :class="{ 'active': activeFormats.bold }"
                                                :color="activeFormats.bold ? 'primary' : undefined"
                                                @click="formatText('bold')" title="Bold">
                                                <v-icon>mdi-format-bold</v-icon>
                                            </v-btn>
                                            <v-btn icon size="small" variant="text"
                                                :class="{ 'active': activeFormats.italic }"
                                                :color="activeFormats.italic ? 'primary' : undefined"
                                                @click="formatText('italic')" title="Italic">
                                                <v-icon>mdi-format-italic</v-icon>
                                            </v-btn>
                                            <v-btn icon size="small" variant="text"
                                                :class="{ 'active': activeFormats.underline }"
                                                :color="activeFormats.underline ? 'primary' : undefined"
                                                @click="formatText('underline')" title="Underline">
                                                <v-icon>mdi-format-underline</v-icon>
                                            </v-btn>
                                        </div>
                                        <v-divider></v-divider>
                                        <div ref="editorContent" contenteditable="true" class="editor-content"
                                            :class="{ 'error': messageError }"
                                            placeholder="Enter testimonial message..." @input="handleEditorInput"
                                            @focus="updateToolbarState" @keyup="updateToolbarState" @paste="handlePaste"
                                            @keydown="limitCharacters" @mouseup="updateToolbarState"
                                            @click="updateToolbarState"></div>
                                    </v-card>
                                    <div class="d-flex justify-space-between align-center mt-1">
                                        <div v-if="messageError" class="text-error text-caption">
                                            {{ messageError }}
                                        </div>

                                        <div class="text-caption text-grey-darken-1 ml-auto">
                                            {{ characterCount }}/{{ maxCharacters }}
                                        </div>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn color="grey" variant="text" @click="handleClose" :disabled="loading">
                        Cancel
                    </v-btn>
                    <v-btn color="primary" variant="flat" :disabled="loading" :loading="loading" @click="handleSubmit">
                        Update Testimonial
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
import { edit_testimonial } from "@/graphql/mutations";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

const client = generateClient();

export default {
    name: "EditTestimonialDialog",
    components: {
        SnackbarComponent,
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        testimonial: {
            type: Object,
            default: null,
        },
    },
    emits: ["update:modelValue", "updated"],
    data() {
        return {
            loading: false,
            formData: {
                testimonial_description: "",
                testimonialsUserDesignation: "",
            },
            maxCharacters: 250,
            characterCount: 0,
            activeFormats: {
                bold: false,
                italic: false,
                underline: false,
            },
            isUpdatingEditor: false,
            messageError: "",
            snackbar: {
                show: false,
                message: "",
                color: "success",
            },
        };
    },
    computed: {
        dialog: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit("update:modelValue", value);
            },
        },
    },
    watch: {
        modelValue(val) {
            if (val && this.testimonial) {
                this.loadTestimonialData();
            } else if (!val) {
                this.resetForm();
            }
        },

        testimonial: {
            immediate: true,
            handler(newVal) {
                if (newVal && this.modelValue) {
                    this.loadTestimonialData();
                }
            }
        }
    },
    methods: {
        loadTestimonialData() {
            if (!this.testimonial) return;

            this.$nextTick(() => {
                this.formData.testimonialsUserDesignation =
                    this.testimonial.testimonial_user_job_description || "";

                this.formData.testimonial_description =
                    this.testimonial.testimonial_description || "";

                if (this.$refs.editorContent) {
                    this.$refs.editorContent.innerHTML =
                        this.testimonial.testimonial_description || "";
                    this.characterCount =
                        this.$refs.editorContent.innerText.length;
                }
            });
        },
        handleClose() {
            this.dialog = false;
            this.resetForm();
        },
        handlePaste(e) {
            e.preventDefault();

            const editor = this.$refs.editorContent;
            const currentText = editor.innerText || "";

            const pastedText =
                (e.clipboardData || window.clipboardData).getData("text");

            const remaining = this.maxCharacters - currentText.length;

            if (remaining <= 0) return;

            document.execCommand(
                "insertText",
                false,
                pastedText.substring(0, remaining)
            );

            this.$nextTick(() => {
                this.handleEditorInput();
            });
        },

        limitCharacters(e) {
            if (!this.$refs.editorContent) return;

            const text = this.$refs.editorContent.innerText || "";

            const allowedKeys = [
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "ArrowUp",
                "ArrowDown",
                "Tab",
                "Home",
                "End",
                "Enter",
            ];

            if (
                text.length >= this.maxCharacters &&
                !allowedKeys.includes(e.key) &&
                !e.ctrlKey &&
                !e.metaKey
            ) {
                e.preventDefault();
            }
        },
        resetForm() {
            this.formData = {
                testimonial_description: "",
                testimonialsUserDesignation: "",
            };
            this.characterCount = 0;
            if (this.$refs.editorContent) {
                this.$refs.editorContent.innerHTML = "";
            }
            this.messageError = "";
            this.activeFormats = {
                bold: false,
                italic: false,
                underline: false,
            };
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },
        formatText(command) {
            if (!this.$refs.editorContent) return;

            if (document.activeElement !== this.$refs.editorContent) {
                this.$refs.editorContent.focus();
            }

            document.execCommand(command, false, null);

            this.$nextTick(() => {
                this.updateToolbarState();

                if (this.$refs.editorContent) {
                    this.formData.testimonial_description = this.$refs.editorContent.innerHTML;
                    this.handleEditorInput();
                }
            });
        },
        updateToolbarState() {
            this.$nextTick(() => {
                if (!this.$refs.editorContent) return;

                const hasFocus = document.activeElement === this.$refs.editorContent;
                if (!hasFocus) {
                    return;
                }

                try {
                    this.activeFormats.bold = document.queryCommandState('bold');
                    this.activeFormats.italic = document.queryCommandState('italic');
                    this.activeFormats.underline = document.queryCommandState('underline');
                } catch (e) {
                    const selection = window.getSelection();
                    if (selection && selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);
                        const container = range.commonAncestorContainer;
                        const element = container.nodeType === 3 ? container.parentElement : container;

                        if (element) {
                            this.activeFormats.bold = element.tagName === 'B' || element.tagName === 'STRONG' ||
                                window.getComputedStyle(element).fontWeight >= 600 ||
                                element.closest('b, strong') !== null;
                            this.activeFormats.italic = element.tagName === 'I' || element.tagName === 'EM' ||
                                window.getComputedStyle(element).fontStyle === 'italic' ||
                                element.closest('i, em') !== null;
                            this.activeFormats.underline = element.tagName === 'U' ||
                                window.getComputedStyle(element).textDecoration.includes('underline') ||
                                element.closest('u') !== null;
                        }
                    } else {
                        this.activeFormats.bold = false;
                        this.activeFormats.italic = false;
                        this.activeFormats.underline = false;
                    }
                }
            });
        },
        validateEditorContent() {
            if (!this.$refs.editorContent) {
                this.messageError = "Testimonial message is required";
                return false;
            }

            let textContent = this.$refs.editorContent.textContent || this.$refs.editorContent.innerText || '';

            if (!textContent.trim() && this.$refs.editorContent.innerHTML) {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = this.$refs.editorContent.innerHTML;
                textContent = tempDiv.textContent || tempDiv.innerText || '';
            }

            const trimmedContent = textContent.trim();

            if (!trimmedContent) {
                this.messageError = "Testimonial message is required";
                return false;
            } else {
                this.messageError = "";
                return true;
            }
        },
        handleEditorInput() {
            if (this.$refs.editorContent && !this.isUpdatingEditor) {
                const content = this.$refs.editorContent.innerHTML;

                this.characterCount =
                    this.$refs.editorContent.innerText.length;

                this.isUpdatingEditor = true;
                this.formData.testimonial_description = content;

                this.$nextTick(() => {
                    this.isUpdatingEditor = false;
                });

                this.validateEditorContent();
            }
        },
        async handleSubmit() {
            if (!this.validateEditorContent()) {
                return;
            }

            if (!this.testimonial?.testimonial_id) {
                this.snackbar = {
                    show: true,
                    message: "Testimonial ID not found",
                    color: "error",
                };
                return;
            }

            this.loading = true;

            try {
                const input = {
                    testimonial_id: this.testimonial.testimonial_id,
                    testimonial_description: this.formData.testimonial_description,
                    testimonial_user_job_description:
                        this.formData.testimonialsUserDesignation,
                };

                const response = await client.graphql({
                    query: edit_testimonial,
                    variables: {
                        input: input,
                    },
                });

                const responseData = JSON.parse(response.data.edit_testimonial);

                if (responseData.status === "SUCCESS") {
                    this.snackbar = {
                        show: true,
                        message: responseData.status_message || "Testimonial updated successfully!",
                        color: "success",
                    };
                    this.$emit("updated");
                    this.handleClose();
                } else {
                    this.snackbar = {
                        show: true,
                        message: responseData.status_message || "Failed to update Testimonial",
                        color: "error",
                    };
                }
            } catch (error) {
                console.error("Error updating Testimonial:", error);
                this.snackbar = {
                    show: true,
                    message:
                        (error.errors && error.errors[0]?.message) ||
                        error.message ||
                        "An error occurred while updating Testimonial",
                    color: "error",
                };
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.rich-text-editor {
    width: 100%;
}

.editor-container {
    border-radius: 4px;
}

.editor-toolbar {
    display: flex;
    gap: 4px;
    padding: 8px;
    background-color: rgba(0, 0, 0, 0.02);
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.editor-toolbar .v-btn {
    min-width: 32px;
    width: 32px;
    height: 32px;
}

.editor-toolbar .v-btn.active,
.editor-toolbar .v-btn[color="primary"] {
    background-color: rgba(var(--v-theme-primary), 0.2) !important;
    color: rgb(var(--v-theme-primary)) !important;
    border: 1px solid rgba(var(--v-theme-primary), 0.3) !important;
}

.editor-toolbar .v-btn.active .v-icon,
.editor-toolbar .v-btn[color="primary"] .v-icon {
    color: rgb(var(--v-theme-primary)) !important;
}

.editor-content {
    min-height: 120px;
    max-height: 300px;
    overflow-y: auto;
    padding: 12px;
    outline: none;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.5;
    color: rgba(0, 0, 0, 0.87);
}

.editor-content:empty:before {
    content: attr(placeholder);
    color: rgba(0, 0, 0, 0.38);
    pointer-events: none;
}

.editor-content:focus {
    outline: none;
}

.editor-content.error {
    border-color: rgb(var(--v-theme-error));
}
</style>
